// Global
import React from 'react';
import Image from 'next/image';
import { Link } from '@sitecore-jss/sitecore-jss-nextjs';
import { classnames } from 'tailwindcss-classnames';
import { useSearchDictionary } from 'components/helpers/AlgoliaSearch/util';

// Lib
// Components
import SvgIcon from 'components/helpers/SvgIcon/SvgIcon';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import {
  OnLinkDataLayerPushPropsImpl,
  gtmDatalayerPush,
  isExternalLink,
} from 'lib/util/gtm-datalayer';

// Locals
import { LinkA11yWrapperProps } from './_types';
import { makeLinkFieldClone, shouldSuppressLink, getLinkDataLayerDetails } from './_helpers';
import { handleJoinCode } from 'lib/util/join-code-helper';
import { useJoinCode } from 'components/globalContext/JoinCodeContext';
import { isTruthy } from 'lib/util/strings';

// Re-export locals for easy access
export type { LinkA11yWrapperProps };
export { shouldSuppressLink };

const LinkA11yWrapper = ({
  field,
  suppressNewTabIcon,
  srOnlyText,
  suppressLinkText,
  onClick,
  onGtmPush,
  leadingIcon,
  ...props
}: LinkA11yWrapperProps): React.JSX.Element => {
  // Get join code if any
  const joinCode = useJoinCode();
  // Format the field prop to be of type LinkField for consistency
  const { asLinkField, cloneLinkField, text } = makeLinkFieldClone({ field, suppressLinkText });

  // We need to split up the querystring if there is one in the href
  const [hrefWithoutQuery, query] = cloneLinkField.value.href?.trim()?.split('?') ?? [];

  const dict = useSearchDictionary();
  const isExperienceEditor = useExperienceEditor();

  const openInSameTab = dict.openinSameWindow ? dict.openinSameWindow : 'openSameWindow';
  const openSameWindow =
    asLinkField.value?.target && asLinkField.value?.target === openInSameTab ? true : false;
  // If there's a query, remove it from the href field, and add it to the querystring field instead
  if (query) {
    // Query from href
    const hrefQuery = new URLSearchParams(query);
    // Query from explicit querystring
    const otherQuery = new URLSearchParams(cloneLinkField.value.querystring || '');
    // Combine the two, prioritizing the explicitly set one
    const combinedQuery = new URLSearchParams({
      ...Object.fromEntries(hrefQuery),
      ...Object.fromEntries(otherQuery),
    });
    // Update the field
    cloneLinkField.value.href = hrefWithoutQuery;
    cloneLinkField.value.querystring = combinedQuery.toString();
  }

  handleJoinCode({ a: cloneLinkField, joinCode });
  // Figure out link and data layer details
  const linkIsExternal = isExternalLink({ link: cloneLinkField, openSameWindow });

  // Sitecore media should open in a new tab, but it should not have an external link icon
  const isMedia = cloneLinkField.value.linktype === 'media' && !openSameWindow;
  const openInNewTab = linkIsExternal || isMedia;
  // Force new tab
  if (openInNewTab) {
    cloneLinkField.value.target = '_blank';
  } else {
    cloneLinkField.value.target = '_self';
  }

  if (openInNewTab && !linkIsExternal && !cloneLinkField.value.rel) {
    // Sitecore adds rel="noopener noreferrer" to links with target="_blank"
    // assuming they are external links.  But since media items open in new tab, but are not external
    // we want to prevent that, so we set a rel value of blank
    cloneLinkField.value.rel = ' ';
  }

  // Don't print the link if not in experience editor and no data is present
  if (
    shouldSuppressLink({
      isExperienceEditor,
      suppressLinkText: isTruthy(suppressLinkText),
      link: cloneLinkField,
    })
  ) {
    return <></>;
  }

  /**
   * Handles link click events
   */
  const linkOnClickHandler = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isExperienceEditor) {
      return onClick ? onClick(e) : true;
    }
    // Infer initial link push data
    const activeDataLayerInfo = getLinkDataLayerDetails({
      isExternalLink: linkIsExternal,
      link: cloneLinkField,
      hrefWithoutQuery,
      e,
    });
    const gtmPushProps = new OnLinkDataLayerPushPropsImpl(e, activeDataLayerInfo, cloneLinkField);
    if (onGtmPush) {
      onGtmPush(gtmPushProps);
    }
    // If the custom handler did not supress processing and we have active data layer info
    if (!gtmPushProps.defaultPrevented && activeDataLayerInfo) {
      // Then push it
      gtmDatalayerPush(activeDataLayerInfo);
    }
    // Call any custom link onClick logic here
    if (onClick && !e.defaultPrevented) {
      return onClick(e);
    }
    return true;
  };

  // In experience editor, do not pass any children but retain basic styling
  // so that double components do not appear when using <Link>
  if (isExperienceEditor || suppressLinkText) {
    if (leadingIcon && isExperienceEditor) {
      // Using <img> to preserve layout inside XP Editor's field chrome.
      return (
        <span className={classnames('flex', 'items-center')}>
          <span className={classnames('mr-3', 'inline-block', 'shrink-0')}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={leadingIcon} alt="" aria-hidden="true" width={24} height={24} />
          </span>
          <Link
            field={cloneLinkField}
            {...props}
            showLinkTextWithChildrenPresent={false}
            onClick={linkOnClickHandler}
          />
        </span>
      );
    }
    return (
      <Link
        field={cloneLinkField}
        {...props}
        showLinkTextWithChildrenPresent={false}
        onClick={linkOnClickHandler}
      />
    );
  }

  return (
    <Link
      field={cloneLinkField}
      {...props}
      showLinkTextWithChildrenPresent={false}
      onClick={linkOnClickHandler}
    >
      {leadingIcon && (
        <span className={`${classnames('mr-3', 'flex', 'shrink-0')}`}>
          <Image src={leadingIcon} alt="" aria-hidden="true" width={24} height={24} />
        </span>
      )}
      {text}
      {props.children}
      {srOnlyText && <span className="sr-only">{srOnlyText}</span>}
      {openInNewTab && !suppressNewTabIcon && (
        <>
          <span className="sr-only">
            {/* Preserve a single space character before SR Tab Text */}
            {' (Opens in a new tab)'}
          </span>
          <SvgIcon
            icon="new-tab"
            size="em"
            viewBoxSize="0 0 48 48"
            className={classnames('ml-2')}
          />
        </>
      )}
    </Link>
  );
};

export default LinkA11yWrapper;
